// ** React Imports
import { List, ListItem, ListItemText } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Close from 'mdi-material-ui/Close';
import { Fragment } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';

// Styled component for the upload image inside the dropzone area
const Img = styled('img')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginRight: theme.spacing(10)
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(4)
  },
  [theme.breakpoints.down('sm')]: {
    width: 250
  }
}))

// Styled component for the heading inside the dropzone area
const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(4)
  }
}))

const ThumbImg = styled('img')({
  display: 'flex',
  height: '60px',
  width: '60px',
  borderRadius: '10px'
});

const ThumbFile = styled('div')({
  display: 'flex',  
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '0 10px',
});

const FileUploaderSingle = ({files, setFiles, update=false, readonly=false}) => {

  // ** Hook
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    multiple: false,
    maxSize: 10000000,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
    },
    onDrop: acceptedFiles => {
      if (acceptedFiles.length === 0) {
        toast.error('지원하지 않는 파일 형식입니다')
        return;
      }

      setFiles(acceptedFiles)
    },
    onDropRejected: (error) => {
      if(error[0].errors[0].code == "too-many-files") {
        toast.error('복수의 파일 업로드는 허용되지 않습니다')
      }
      else if(error[0].errors[0].code == "file-too-large") {
        toast.error('파일 업로드는 최대 5MB 까지 허용됩니다')
      }
    }
  })

  const handleRemoveFile = file => {
    const uploadedFiles = files
    const filtered = uploadedFiles.filter(i => i.name !== file.name)
    setFiles([...filtered])
  }

  const getIconType = file => {
    return file.toLowerCase().endsWith('.pdf') ? '/images/icons/file-icons/pdf.png' : file
  }

  const showIcon = (file) => {
    if (update) {
      return (
        <ThumbImg
          key={file}
          alt={file}
          className="single-file-image"
          src={getIconType(file)}
        />
      );
    }
  
    if (file.type === 'application/pdf') {
      return (
        <img
          alt={file.name}
          className="single-file-image"
          src="/images/icons/file-icons/pdf.png"
        />
      );
    }
  
    return (
      <ThumbImg
        key={file.name}
        alt={file.name}
        className="single-file-image"
        src={URL.createObjectURL(file)}
      />
    );
  };
  
  const showInfo = (file) => {
    if (update) {
      return (
        <Typography className='file-name' sx={{ml:2}}>{file}</Typography>
      );
    }

    return (
      <ThumbFile>
          <Typography className='file-name' >{file.name}</Typography>
          <Typography className='file-size' variant='body2'>
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} MB`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} KB`}
          </Typography>
        </ThumbFile>
    );
  };  

  const fileList = files.map((file, index) => (    
    <ListItem key={index}>
      <div className='file-details' style={{display: 'flex', alignItems: 'center'}}>
        {
          showIcon(file)          
        }
        {
          showInfo(file)
        }
        {
          !readonly && <IconButton onClick={() => handleRemoveFile(file)}>
            <Close fontSize='small' />
          </IconButton>
        }
      </div>      
    </ListItem>
  ))

  return (
    <Fragment>
      { files.length ? null :
        <Box {...getRootProps({ className: 'dropzone' })} sx={acceptedFiles.length ? { height: 200 } : {}}>
          <input {...getInputProps()} />
          <Box sx={{ display: 'flex', flexDirection: ['column', 'column', 'row'], alignItems: 'center' }}>
            <Img width={200} alt='Upload img' src='/images/misc/upload.png' />
            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: ['center', 'center', 'inherit'] }}>
              <HeadingTypography variant='h5'>첨부파일
              </HeadingTypography>
              <List sx={{mb: '8px'}}>
                <ListItemText primary="파일용량은 5MB 이하이며, 1개 파일 업로드 가능합니다."/>
                <ListItemText primary="등록 가능 파일 : png, jpg, jpeg, gif, pdf"/>
              </List>
              여기를 클릭하여 파일을 첨부하거나 파일을 마우스로 끌어오세요
            </Box>
          </Box>
        </Box>
      }
      {files.length ? (
        <Fragment>
          <List>{fileList}</List>
        </Fragment>
      ) : null}
    </Fragment>
  )
}

export default FileUploaderSingle
